/**
 * Display formatting for US addresses.
 * Street/city/subdivision: Title Case. State: uppercase abbreviation (TX).
 */

const SHORT_DIRECTIONALS = new Set(['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']);

function titleCaseToken(token: string): string {
  if (!token) {
    return '';
  }

  return token
    .split('-')
    .map((part) => {
      if (!part) {
        return part;
      }
      const lower = part.toLowerCase();
      if (SHORT_DIRECTIONALS.has(lower)) {
        return lower.toUpperCase();
      }
      // Common Scottish/Irish prefix in street names (McPherson, McKinney).
      if (/^mc[a-z]/.test(lower) && lower.length > 2) {
        return `Mc${lower.charAt(2).toUpperCase()}${lower.slice(3)}`;
      }
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('-');
}

/** Title-case whitespace-separated words (keeps N/S/E/W/NE… uppercase). */
export function toTitleCaseWords(value: unknown): string {
  const text = String(value ?? '').trim();
  if (!text) {
    return '';
  }

  return text
    .split(/\s+/)
    .filter(Boolean)
    .map(titleCaseToken)
    .join(' ');
}

/** Two-letter state codes → uppercase; longer names → title case. */
export function formatUsStateDisplay(state: unknown): string {
  const raw = String(state ?? '').trim();
  if (!raw) {
    return '';
  }
  if (raw.length <= 2) {
    return raw.toUpperCase();
  }
  return toTitleCaseWords(raw);
}

export function formatUsStreetLine(parts: {
  number?: unknown;
  street?: unknown;
  unit?: unknown;
}): string {
  const number = String(parts.number ?? '').trim();
  const street = toTitleCaseWords(parts.street);
  const unit = String(parts.unit ?? '').trim();
  const line = [number, street].filter(Boolean).join(' ').trim();
  if (!unit) {
    return line;
  }
  return `${line} ${unit}`.trim();
}

export function formatUsCityStateZip(parts: {
  city?: unknown;
  state?: unknown;
  zip?: unknown;
}): string {
  const city = toTitleCaseWords(parts.city);
  const state = formatUsStateDisplay(parts.state);
  const zip = String(parts.zip ?? '').trim();
  const cityState = [city, state].filter(Boolean).join(', ');
  return (cityState + (zip ? ` ${zip}` : '')).trim();
}
